import DragIndicator from '@mui/icons-material/DragIndicator'
import Print from '@mui/icons-material/Print'
import PrintDisabled from '@mui/icons-material/PrintDisabled'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { ButtonBase, IconButton, Switch, Collapse, Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'

function SortableItem({ 
  item, 
  index, 
  onSectionToggle, 
  onPrintableToggle, 
  onSectionPrintOnlyEditorOnlyChange,
  hidePrintOnlyEditorOnlyToggles = false,
  hideReordering,
  reportType,
  sectionSubsectionsMap,
  expandedSections,
  onToggleExpansion,
  onToggleSubsectionExclusion,
  onToggleSubsectionPrintable,
  extraExcludedSubSectionsInSections,
  fetchSectionEffectiveLayout,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(item?.id ?? `fallback-${index}`) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderBottom: '1px solid #cecece',
    padding: '8px 0',
    background: isDragging ? '#f0f0f0' : 'white',
    opacity: isDragging ? 0.5 : 1,
  }

  // Screening report section cannot be disabled - it's always enabled when data exists
  const isScreeningReportSection = item?.id === 'screeningReportData'
  // In editor drawer: print-only sections cannot be enabled in editor; editor-only sections cannot show in print
  const enableSwitchDisabled = isScreeningReportSection || (hidePrintOnlyEditorOnlyToggles && item?.printOnly)
  const printButtonDisabled = hidePrintOnlyEditorOnlyToggles && item?.editorOnly
  const isDisabled = isScreeningReportSection
  
  // Try to get section code - use defCode if available, otherwise use id
  // The API endpoint expects the section definition code
  const sectionCode = item?.defCode || item?.id
  
  // Get subsections from multiple possible sources
  let subsections = []
  
  // 1. From sectionSubsectionsMap (populated from API response)
  if (sectionSubsectionsMap[sectionCode]) {
    subsections = sectionSubsectionsMap[sectionCode]
  }
  // 2. Direct subSections array on the item (e.g., diagnosis, drug_advice)
  else if (item?.subSections && Array.isArray(item.subSections)) {
    subsections = item.subSections
  }
  // 3. Nested in emrSectionResponse.section.subSections (e.g., screening-recommendation, patient-history-notes)
  else if (item?.emrSectionResponse?.section?.subSections && Array.isArray(item.emrSectionResponse.section.subSections)) {
    subsections = item.emrSectionResponse.section.subSections
  }
  
  const hasSubsections = subsections.length > 0
  const isExpanded = expandedSections.has(item?.id)
  
  // Use section defCode if available, otherwise use id (per API docs)
  // Section keys are resolved as: 1) defCode (preferred), 2) id
  const sectionKey = item?.defCode || item?.id
  const sectionExclusions = (extraExcludedSubSectionsInSections && extraExcludedSubSectionsInSections[sectionKey]) || []
  const [loadingSubsections, setLoadingSubsections] = React.useState(false)
  const [hasTriedFetching, setHasTriedFetching] = React.useState(false)
  
  // Check if we've already confirmed there are no subsections
  // This happens when we've checked all possible sources and found none:
  // 1. sectionSubsectionsMap has been populated (either from initial fetch or after expansion)
  // 2. item.subSections exists (even if empty array)
  // 3. item.emrSectionResponse.section.subSections exists (even if empty array)
  const hasCheckedAllSources = sectionSubsectionsMap[sectionCode] !== undefined || 
                                (item?.subSections !== undefined && Array.isArray(item.subSections)) || 
                                (item?.emrSectionResponse?.section?.subSections !== undefined && Array.isArray(item.emrSectionResponse.section.subSections))
  const hasNoSubsections = hasCheckedAllSources && !hasSubsections

  const handleToggleExpansion = async () => {
    if (!isExpanded) {
      // If we haven't fetched subsections yet and haven't confirmed there are none, try to fetch them
      if (!hasSubsections && sectionCode && !hasTriedFetching && !hasNoSubsections) {
        setLoadingSubsections(true)
        setHasTriedFetching(true)
        try {
          const fetchedSubsections = await fetchSectionEffectiveLayout(sectionCode, reportType)
          // After fetching, if we still have no subsections, we've confirmed there are none
          // This will be reflected in hasNoSubsections on next render when sectionSubsectionsMap updates
        } catch (error) {
          console.error('Failed to fetch subsections:', error)
        } finally {
          setLoadingSubsections(false)
        }
      }
      onToggleExpansion(item?.id, sectionCode, reportType)
    } else {
      onToggleExpansion(item?.id, sectionCode, reportType)
    }
  }
  
  // Show expand button only if we have subsections, or if we haven't confirmed there are no subsections
  const shouldShowExpandButton = hasSubsections || (!hasNoSubsections && sectionCode)

  // Determine if a subsection is excluded
  // According to API docs, the server resolves subSections[].selected based on 
  // extraIncludedSubSectionsInSections and extraExcludedSubSectionsInSections maps
  // We check both the selected property (from API/server) and our local exclusion map
  // Local exclusion map takes precedence for immediate UI updates
  const isSubsectionExcluded = (subsection) => {
    const subsectionUid = subsection?.uid
    const subsectionCode = subsection?.code
    
    // Check local exclusion map first (for immediate UI updates)
    const isInExclusionMap = sectionExclusions && (
      sectionExclusions.includes(subsectionUid) || 
      (subsectionCode && sectionExclusions.includes(subsectionCode))
    )
    
    if (isInExclusionMap) {
      return true
    }
    
    // If not in exclusion map, check the selected property from API
    if (subsection?.selected !== undefined) {
      return !subsection.selected
    }
    
    // Default to not excluded
    return false
  }
  
  // Calculate selected count (subsections that are selected/not excluded)
  const totalSubsections = subsections.length
  const selectedSubsections = React.useMemo(() => {
    return subsections.filter((subsection) => {
      // Use selected property from API if available (server-resolved)
      if (subsection?.selected !== undefined) {
        return subsection.selected === true
      }
      // Otherwise, check if it's not in the exclusion list
      const subsectionUid = subsection?.uid
      return !(sectionExclusions && sectionExclusions.includes(subsectionUid))
    }).length
  }, [subsections, sectionExclusions])

  const finalStyle = {
    ...style,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0, // Prevent flex items from overflowing
  };
  
  return (
    <div
      ref={setNodeRef}
      style={finalStyle}
    >
      <div 
        className="d-flex justify-content-between align-items-center" 
        style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
        ref={(rowEl) => {
          if (rowEl) {
            // Force flex display
            rowEl.style.setProperty('display', 'flex', 'important');
            rowEl.style.setProperty('flex-direction', 'row', 'important');
          }
        }}
      >
        <div 
          className="d-flex align-items-center gap-1" 
          style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          ref={(leftSectionEl) => {
            if (leftSectionEl) {
              leftSectionEl.style.setProperty('display', 'flex', 'important');
              leftSectionEl.style.setProperty('flex-direction', 'row', 'important');
            }
          }}
        >
          {!hideReordering && !isScreeningReportSection && (
            <div {...attributes} {...listeners} style={{ cursor: 'grab', flexShrink: 0 }}>
              <DragIndicator />
            </div>
          )}
          {!hideReordering && isScreeningReportSection && (
            <div style={{ width: '24px', flexShrink: 0 }} />
          )}
          {shouldShowExpandButton && (
            <IconButton
              size="small"
              onClick={handleToggleExpansion}
              disabled={loadingSubsections}
              sx={{ p: 0.5, flexShrink: 0 }}
            >
              {loadingSubsections ? (
                <CircularProgress size={16} />
              ) : isExpanded ? (
                <ExpandLess />
              ) : (
                <ExpandMore />
              )}
            </IconButton>
          )}
          {!shouldShowExpandButton && <div style={{ width: '32px', flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', flexDirection: 'column' }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item?.title}</div>
            {item?.recommended && (
              <div style={{ fontSize: '12px', color: '#005D91', whiteSpace: 'nowrap' }}>(Recommended)</div>
            )}
            {isScreeningReportSection && (
              <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                (Always shown when screening data exists)
              </div>
            )}
            {hasSubsections && (
              <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
                {selectedSubsections} of {totalSubsections} subsection{totalSubsections !== 1 ? 's' : ''}
              </div>
            )}
            {!hasSubsections && shouldShowExpandButton && !loadingSubsections && !hasNoSubsections && (
              <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                Click to load subsections
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
          <Tooltip title={enableSwitchDisabled ? (item?.printOnly ? 'Print only section – cannot enable in editor' : 'Screening Report section cannot be disabled') : ''}>
            <span>
              <Switch
                checked={item?.selected ?? false}
                onChange={(e) => onSectionToggle(item, e.target.checked)}
                disabled={enableSwitchDisabled}
              />
            </span>
          </Tooltip>
          <Tooltip title={printButtonDisabled ? 'Editor only – cannot show in print' : (item?.printable ? 'Shown in print (click to hide from print)' : 'Hidden from print (click to show)')}>
            <span>
              <IconButton
                sx={{
                  background: 'transparent',
                  color: 'black',
                  p: 1,
                  borderRadius: '40px',
                }}
                size="small"
                disabled={printButtonDisabled}
                onClick={() => !printButtonDisabled && onPrintableToggle(item)}
                title={undefined}
              >
                {item?.printable ? <Print /> : <PrintDisabled />}
              </IconButton>
            </span>
          </Tooltip>
          {onSectionPrintOnlyEditorOnlyChange && !hidePrintOnlyEditorOnlyToggles && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
              <Tooltip title="Show in editor only (hide from print)">
                <span>
                  <Switch
                    size="small"
                    checked={!!item?.editorOnly}
                    disabled={!item?.selected}
                    onChange={(e) => onSectionPrintOnlyEditorOnlyChange(item, { printOnly: false, editorOnly: e.target.checked })}
                    sx={{ transform: 'scale(0.75)' }}
                  />
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap' }}>Editor only</Typography>
              <Tooltip title="Show in print only (hide from editor)">
                <span>
                  <Switch
                    size="small"
                    checked={!!item?.printOnly}
                    disabled={!item?.selected}
                    onChange={(e) => onSectionPrintOnlyEditorOnlyChange(item, { printOnly: e.target.checked, editorOnly: false })}
                    sx={{ transform: 'scale(0.75)' }}
                  />
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap' }}>Print only</Typography>
            </Box>
          )}
        </div>
      </div>
      
      {/* Subsections - only show if subsections exist */}
      {hasSubsections && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 4, pr: 2, pt: 1, pb: 2, bgcolor: '#f9f9f9', borderLeft: '2px solid #ddd' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#555' }}>
              Subsections:
            </div>
            {loadingSubsections ? (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <CircularProgress size={20} />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Loading subsections...
                </div>
              </div>
            ) : subsections.length === 0 ? (
              <div style={{ padding: '8px', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                No subsections available for this section.
              </div>
            ) : (
              subsections.map((subsection) => {
              const subsectionUid = subsection?.uid
              const isExcluded = isSubsectionExcluded(subsection)
              
              return (
                <div
                  key={subsectionUid}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px' }}>{subsection?.title || subsectionUid}</div>
                    {subsection?.description && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                        {subsection.description}
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                    <Switch
                      checked={!isExcluded}
                      onChange={(e) => {
                        // Pass item.id to find the section, function will use defCode || id as the key
                        // Use subsection code if available, otherwise uid (per API docs)
                        const subsectionKey = subsection?.code || subsectionUid
                        onToggleSubsectionExclusion(reportType, item?.id, subsectionKey, !e.target.checked)
                      }}
                      size="small"
                      title={isExcluded ? 'Include this subsection' : 'Exclude this subsection'}
                    />
                    {onToggleSubsectionPrintable && (
                      <IconButton
                        sx={{
                          background: 'transparent',
                          color: 'black',
                          p: 1,
                          borderRadius: '40px',
                        }}
                        size="small"
                        onClick={() => {
                          // Pass item.id to find the section, function will use defCode || id as the key
                          // Use subsection code if available, otherwise uid (per API docs)
                          const subsectionKey = subsection?.code || subsectionUid
                          onToggleSubsectionPrintable(reportType, item?.id, subsectionKey)
                        }}
                        title={subsection?.printable !== false ? 'Disable printing' : 'Enable printing'}
                      >
                        {subsection?.printable !== false ? <Print /> : <PrintDisabled />}
                      </IconButton>
                    )}
                  </div>
                </div>
              )
            })
            )}
          </Box>
        </Collapse>
      )}
    </div>
  )
}

export const DraggableEmrSectionList = ({
  emrSections,
  onDragEnd,
  onSectionToggle,
  onPrintableToggle,
  onSectionPrintOnlyEditorOnlyChange,
  hidePrintOnlyEditorOnlyToggles = false,
  hideReordering = false,
  reportType,
  sectionSubsectionsMap = {},
  expandedSections = new Set(),
  onToggleExpansion,
  onToggleSubsectionExclusion,
  onToggleSubsectionPrintable,
  extraExcludedSubSectionsInSections = {},
  fetchSectionEffectiveLayout,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragEnd(event) {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = emrSections.findIndex((item, idx) => {
        const id = String(item?.id ?? `fallback-${idx}`)
        return id === active.id
      })
      const newIndex = emrSections.findIndex((item, idx) => {
        const id = String(item?.id ?? `fallback-${idx}`)
        return id === over.id
      })

      if (oldIndex !== -1 && newIndex !== -1) {
        // Call the original onDragEnd with react-beautiful-dnd compatible format
        onDragEnd({
          source: { index: oldIndex },
          destination: { index: newIndex },
        })
      }
    }
  }

  const items = emrSections?.map((item, index) => String(item?.id ?? `fallback-${index}`)) || []

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="pt-4">
          {emrSections?.map((emrSectionItem, emrSectionItemIndex) => (
            <SortableItem
              key={`${emrSectionItem?.id}-${emrSectionItemIndex}`}
              item={emrSectionItem}
              index={emrSectionItemIndex}
              onSectionToggle={onSectionToggle}
              onPrintableToggle={onPrintableToggle}
              onSectionPrintOnlyEditorOnlyChange={onSectionPrintOnlyEditorOnlyChange}
              hidePrintOnlyEditorOnlyToggles={hidePrintOnlyEditorOnlyToggles}
              hideReordering={hideReordering}
              reportType={reportType}
              sectionSubsectionsMap={sectionSubsectionsMap}
              expandedSections={expandedSections}
              onToggleExpansion={onToggleExpansion}
              onToggleSubsectionExclusion={onToggleSubsectionExclusion}
              onToggleSubsectionPrintable={onToggleSubsectionPrintable}
              extraExcludedSubSectionsInSections={extraExcludedSubSectionsInSections}
              fetchSectionEffectiveLayout={fetchSectionEffectiveLayout}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
